import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserDto } from "src/api/users/type";
import { useAuth } from "src/hooks";
import type { Id } from "src/types/id";
import { Mutation } from "src/types/mutation";
import { Query } from "src/types/query";
import type { User, UserPreferenceKey, UserPreferenceKeyCreate } from "src/types/user";
import { axios } from "src/utils/axios";

interface ResetUserPasswordProps {
    token: string;
    newPassword: string;
}

export const useResetUserPassword = (): UseMutationResult<void, unknown, ResetUserPasswordProps> =>
    useMutation({
        mutationKey: [Mutation.ResetUserPassword],

        mutationFn: async (props) => {
            await axios.post<void>("/auth/reset-password", props);
        },
    });

interface UpdateUserProps {
    userId: Id;
    user: UserDto;
}

export const useUpdateUser = (): UseMutationResult<User, unknown, UpdateUserProps> => {
    const clientQuery = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationKey: [Mutation.UpdateUser],
        mutationFn: async (props) =>
            (await axios.patch<{ user: User }>(`/user/${props.userId}`, props.user)).data.user,

        onSuccess: (_data, variables) => {
            clientQuery.invalidateQueries({
                queryKey: [Query.Users, user.id],
            });
            clientQuery.invalidateQueries({
                queryKey: [Query.User, user.id, variables.userId],
            });
        },
    });
};

export function useMutationUserPreferences(): UseMutationResult<
    { preferences: UserPreferenceKey[] },
    unknown,
    { userId?: number; preferences: UserPreferenceKeyCreate[] }
> {
    const clientQuery = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationKey: [Mutation.UpdateUserPreferences],

        mutationFn: async ({ userId = user.id, preferences }) =>
            (
                await axios.patch<{ preferences: UserPreferenceKey[] }>(
                    `/user/${userId}/preferences`,
                    {
                        preferences,
                    }
                )
            ).data,

        onSuccess: (_, { userId = user.id }) => {
            clientQuery.invalidateQueries({
                queryKey: [Query.UserPreferences, user.id, userId],
            });
        },
    });
}

export const useDeleteUser = (): UseMutationResult<Id, unknown, Id> => {
    const clientQuery = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationKey: [Mutation.DeleteUser],
        mutationFn: async (id: Id) => (await axios.delete<Id>(`/user/${id}`)).data,

        onSuccess: () => {
            clientQuery.invalidateQueries({
                queryKey: [Query.Users, user.id],
            });
        },
    });
};

export const useCreateUser = (): UseMutationResult<User, unknown, UserDto> => {
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();

    return useMutation({
        mutationKey: [Mutation.CreateUser],
        mutationFn: async (userToCreate: UserDto) =>
            (await axios.post<{ user: User }>("/user", userToCreate)).data.user,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [Query.OrganizationUsers, currentUser.id],
            });
            // Invalidate the general users query to ensure all user lists (without filters) are updated
            await queryClient.invalidateQueries({ queryKey: [Query.Users], exact: true });
        },
    });
};
